export type JwtPayload = {
    sub?: string
    email?: string
    name?: string
    auth_time?: string | number
    exp?: string | number
    [claim: string]: unknown
}

export const parseJwtToken = (token: string | null): JwtPayload => {
    if (!token) return {}

    try {
        // JWT payloads are base64url encoded and hold UTF-8 bytes, while atob only accepts
        // standard base64 and returns a binary string. Tokens carrying '-' or '_' threw, and
        // non-ASCII claims such as an accented name came back mangled.
        const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
        const bytes = Uint8Array.from(atob(payload), (character) => character.charCodeAt(0))
        return JSON.parse(new TextDecoder().decode(bytes))
    } catch (error) {
        console.error('Failed to decode ID token:', error)
        return {}
    }
}

/**
 * Generates a short numeric representation of a token.
 *
 * @param token - The token as a string.
 * @returns A numeric hash of the token, modded by 10000.
 *
 * @example
 * const token = "exampleToken";
 * const shortToken = getShortToken(token);
 * console.info(shortToken); // e.g., 1234
 */
export const getShortToken = (token: string) => {
    return token.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 10000
}

/**
 * Converts a Unix timestamp to a locale string.
 *
 * @param timestamp - The Unix timestamp in seconds.
 * @returns A locale string representation of the timestamp, or "undefined" if no timestamp is provided.
 *
 * @example
 * const timestamp = 1672531200;
 * const localeString = timestampToLocaleString(timestamp);
 * console.info(localeString); // e.g., "1/1/2023, 12:00:00 AM"
 */
export const timestampToLocaleString = (timestamp?: string | number) => {
    if (!timestamp) return 'undefined'

    return new Date(Number(timestamp) * 1000).toLocaleString()
}
